import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { Nullable } from '#/utils/types';

const SESSION_RECORDING_ENVS = new Set([
  'prod',
  'testnet',
  'staging',
  'nightly',
]);

const isSessionRecordingEnabled = () => {
  return SESSION_RECORDING_ENVS.has(ENVIRONMENT);
};

const isMaskedInputEnabled = () => {
  return false;
};

const setup = () => {
  if (ENABLE_SENTRY === 'true') setupSentry();
  if (ENABLE_DATADOG === 'true') setupDatadog();
  console.info(`Paradex Webapp ver. ${RELEASE_NAME}`); // eslint-disable-line no-console
};

const setupSentry = () => {
  Sentry.init({
    dsn: 'https://1747b11cd4234ea5a44d1d0c1ffc76dc@o169143.ingest.sentry.io/4504532046381056',
    environment: ENVIRONMENT,
    release: RELEASE_NAME,
    attachStacktrace: true,
    // Session replay disabled in Sentry until it's proven useful.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event, hint) {
      const error = hint.originalException;
      const message =
        error == null
          ? ''
          : typeof error === 'string'
          ? error
          : (error as Error).message;
      // Ignore "Failed to fetch" errors as there's nothing to do about them
      if (/Failed to fetch/iu.exec(message)) return null;
      return event;
    },
    integrations: [
      new Sentry.Replay({
        maskAllInputs: isMaskedInputEnabled(),
        maskAllText: false,
      }),
    ],
  });
};

const setupDatadog = () => {
  const SITE = 'datadoghq.com';
  /** Allows DD to run when Paradex runs in an iframe */
  const USE_CROSS_SITE_SESSION_COOKIE = true;

  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    site: SITE,
    service: DATADOG_SERVICE_NAME,
    env: ENVIRONMENT,
    version: RELEASE_NAME,
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    useCrossSiteSessionCookie: USE_CROSS_SITE_SESSION_COOKIE,
  });
  datadogRum.init({
    applicationId: DATADOG_APPLICATION_ID,
    clientToken: DATADOG_CLIENT_TOKEN,
    site: SITE,
    service: DATADOG_SERVICE_NAME,
    env: ENVIRONMENT,
    version: RELEASE_NAME,
    sessionSampleRate: 100,
    sessionReplaySampleRate: isSessionRecordingEnabled() ? 100 : 0,
    trackUserInteractions: true,
    allowedTracingUrls: [API_URL, API_URL_WS],
    defaultPrivacyLevel: isMaskedInputEnabled() ? 'mask-user-input' : 'allow',
    trackResources: true,
    trackLongTasks: true,
    useCrossSiteSessionCookie: USE_CROSS_SITE_SESSION_COOKIE,
  });
  datadogRum.startSessionReplayRecording();
};

interface ExceptionLogger {
  (error: unknown, details?: Record<string, unknown>): void;
}

const logException: ExceptionLogger = (error, details) => {
  if (ENVIRONMENT === 'test-runner') {
    throw error;
  }

  if (ENABLE_DATADOG === 'true') {
    logDataDogException(error, details);
  }

  if (ENABLE_SENTRY === 'true') {
    logSentryException(error, details);
  }

  if (NODE_ENV === 'development') {
    logConsoleException(error, details);
  }
};

const logEvent = (message: string, details?: Record<string, unknown>) => {
  if (ENVIRONMENT === 'test-runner') {
    return;
  }

  if (ENABLE_DATADOG === 'true') {
    datadogLogs.logger.info(message, details);
  }

  if (ENABLE_SENTRY === 'true') {
    Sentry.addBreadcrumb({
      type: 'info',
      level: 'info',
      message,
      data: details,
      timestamp: Date.now() / 1000,
    });
  }

  if (NODE_ENV === 'development' || String(FE_DOMAIN).includes('localhost')) {
    // eslint-disable-next-line no-console
    console.info(message, details);
  }
};

const logSentryException: ExceptionLogger = (error, details = {}) => {
  Sentry.captureException(error, { extra: details });
};

const logDataDogException: ExceptionLogger = (error, details = {}) => {
  datadogLogs.logger.error('Exception', details, error as Error | undefined);
};

/** How many times to look for causes when printing error causes */
const CAUSES_DEPTH = 5;

const logConsoleException: ExceptionLogger = (error, details = {}) => {
  /* eslint-disable no-console */
  console.error(error, details);

  // Workaround to display causes until Chrome supports it
  // Tracked in https://bugs.chromium.org/p/chromium/issues/detail?id=1211260
  for (
    let { cause } = error as Error, i = 0;
    cause != null && i <= CAUSES_DEPTH;
    cause = (cause as Error).cause, i++
  ) {
    console.error('Caused by:', cause);
  }
  /* eslint-enable no-console */
};

interface User {
  id: string;
  ethereumAddress: Nullable<string>;
  starknetAddress: Nullable<string>;
  [key: string]: unknown;
}

const setUser = (user: User) => {
  datadogRum.setUser(user);
  Sentry.setUser(user);
};

const clearUser = () => {
  datadogRum.clearUser();
  Sentry.setUser(null);
};

export { setup, logException, logEvent, setUser, clearUser };
